// @flow

import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router';
import FontAwesome from 'react-fontawesome';
import moment from 'moment';
import { css } from 'glamor';
import { Row, Col, Image } from 'react-bootstrap';

import Genre from '~/components/content/game/Genre';
import Contributor from '~/components/global/Contributor';
import Flag from '~/components/global/Flag';
import Tooltip from '~/components/global/Tooltip';
import Score from '~/components/global/Score';

import type { Review as ReviewType } from '~/types';

const versions = {
    jp: 'Japanese',
    us: 'American',
    eu: 'European',
};

const gameDetail = css({
    display: 'inline-block',
    paddingRight: '15px',
    fontWeight: 500,

    '@media(max-width: 768px)': {
        display: 'block',
        fontWeight: 300,
        fontSize: '0.9em',
    },
});

const icon = css({
    marginRight: '10px',

    '@media(max-width: 768px)': {
        display: 'none',
    },
});

const dataIcon = css({
    marginRight: '13px',

    '@media(max-width: 768px)': {
        display: 'none',
    },
});

const flagIcon = css({
    marginRight: '10px',
});

const dateRule = css({
    fontSize: '0.8em',

    '@media(max-width: 768px)': {
        whiteSpace: 'nowrap',
    },
});

const author = css({
    color: '#676767',
    fontSize: '0.9em',

    '& .authorAvatar': {
        height: '35px',
        width: '35px',
    },

    '@media(max-width: 768px)': {
        display: 'none',
    },
});

const authorInline = css({
    '@media(min-width: 769px)': {
        display: 'none',
    },
});

const reviewBlock = css({
    textAlign: 'left',
    lineHeight: '20px',
    marginTop: '-1px',

    '@media(max-width: 768px)': {
        marginTop: '-1px',
    },
});

const platformName = css({
    fontSize: '0.8em',
    color: '#8e8989',
});

const divider = css({
    ':before': {
        '@media(max-width: 768px)': {
            left: '-5px',
            bottom: '0px',
            top: 0,
        },

        backgroundColor: 'rgb(235, 235, 235)',
        position: 'absolute',
        bottom: '30px',
        top: '30px',
        left: '-23px',
        content: '" "',
        width: '2px',
    },
});

const reviewData = css({
    minHeight: '150px',
    marginTop: '28px',

    '@media(max-width: 768px)': {
        minHeight: '80px',
        marginTop: '-28px',
    },
});

const reviewDataTitle = css({
    fontSize: '1em',
    opacity: 0.8,
    fontWeight: 900,
    color: '#e44122',
    marginTop: 0,

    '@media(max-width: 768px)': {
        display: 'none',
    },
});

const reviewDetail = css({
    margin: '10px',
    marginLeft: 0,
    fontWeight: 300,

    '@media(max-width: 768px)': {
        margin: 0,
    },
});

const data = css({
    marginTop: '18px',

    '@media(max-width: 768px)': {
        marginTop: 0,
    },
});

const imageRule = css({
    height: '100%',
    width: '100%',
});

const imageSmall = css({
    height: '70px',
    maxWidth: '70px',
});

const smallerTitle = css({
    fontSize: '1em',
});

const summaryText = css({
    marginTop: '5px',
    marginRight: '45px',
    minHeight: '100px',
    height: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'justify',

    '@media(max-width: 768px)': {
        display: 'none',
    },
});

const reviewRule = css({
    paddingTop: '15px',
    paddingBottom: '15px',
    marginBottom: '10px',
});

const gameTitle = css({
    fontSize: '1.2em',
    fontWeight: 900,
    color: 'rgb(228, 65, 34)',
    marginTop: 0,
    marginBottom: '5px',
});

const genreListRule = css({
    paddingLeft: 0,
    listStyle: 'none',
});

const genreRule = css({
    display: 'inline-block',
    marginLeft: '10px',

    '@media(max-width: 768px)': {
        marginLeft: 0,
    },
});

const versionRule = css({
    '@media(max-width: 768px)': {
        fontSize: '0.8em',
    },
});

const publishedRule = css({
    '@media(max-width: 768px)': {
        display: 'none',
    },
});

@observer
export default class Review extends React.Component {
    props: {
        review: ReviewType,
        displayAbout?: boolean,
        summary?: string,
    };

    renderSummary() {
        const { summary, review } = this.props;

        switch (summary) {
            case 'full':
                return (
                    <div>
                        <span>
                            <span { ...gameDetail }>
                                <span { ...icon }>
                                    <FontAwesome name="building" />
                                </span>
                                <span>
                                    {review.game.developer.name}
                                </span>
                            </span>
                            <span { ...gameDetail }>
                                <span { ...icon }>
                                    <FontAwesome name="calendar" />
                                </span>
                                <span>
                                    {moment(
                                        new Date(
                                            review.game.releaseDates.jp ||
                                                review.game.releaseDates.mvs
                                        )
                                    ).format('DD MMM YYYY')}
                                </span>
                            </span>
                            <span { ...gameDetail }>
                                <span>
                                    <ul { ...genreListRule }>
                                        {review.game.genres.map(genre => (
                                            <li { ...genreRule } key={ genre.id }>
                                                <Genre genre={ genre } />
                                            </li>
                                        ))}
                                    </ul>
                                </span>
                            </span>
                        </span>
                        <p { ...summaryText }>
                            {review.content.brief.md}
                        </p>
                    </div>
                );

            case 'semi':
                return (
                    <div>
                        <span>
                            <span { ...platformName } { ...gameDetail }>
                                {review.platform.name}
                            </span>
                            <span
                                { ...gameDetail }
                                data-tip
                                data-for={ `time-tooltip${review.id}` }
                            >
                                <Tooltip
                                    id={ `time-tooltip${review.id}` }
                                    place="right"
                                >
                                    <span>
                                        {moment(
                                            new Date(review.publishedAt)
                                        ).format('DD MMM YYYY')}
                                    </span>
                                </Tooltip>
                                <span { ...icon }>
                                    <FontAwesome name="clock-o" />
                                </span>
                                <span { ...dateRule }>
                                    {`${moment(new Date(review.publishedAt)).fromNow()}`}
                                </span>
                            </span>
                            <span { ...gameDetail }>
                                <span { ...icon }>
                                    <FontAwesome name="user" />
                                </span>
                                <span>
                                    {`by ${review.author.name}`}
                                </span>
                            </span>
                        </span>
                        <p { ...summaryText }>
                            {review.content.brief.md}
                        </p>
                    </div>
                );

            case 'short':
                return (
                    <div { ...reviewBlock }>
                        <span { ...platformName }>
                            {review.platform.name}
                        </span>
                        <div>
                            <Score
                                score={ review.objectiveScore.score }
                                maxScore={ 100 }
                                small
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    render() {
        const { review, displayAbout, summary } = this.props;
        const rules = {
            image: summary === 'short' ? imageSmall : imageRule,
            title: summary === 'short' ? smallerTitle : null,
        };
        const imageUrl = (() => {
            const original =
                (review.game.thumbnail && review.game.thumbnail.secure_url) ||
                (review.game.image && review.game.image.secure_url);
            const parts = original.split('/');
            parts[parts.length - 2] = summary === 'short' ?
                'w_70,h_70,c_fill' :
                'w_165,h_120,c_fill';

            return parts.join('/');
        })();

        if (!review || !review.game) {
            return null;
        }

        return (
            <div { ...reviewRule }>
                <Row>
                    <Col md={ displayAbout ? 2 : 3 } xs={ displayAbout ? 3 : 4 }>
                        <Link to={ `/reviews/${review.slug}` }>
                            <Image
                                src={ imageUrl }
                                alt={ review.game.name }
                                title={ review.game.name }
                                { ...rules.image }
                            />
                        </Link>
                    </Col>

                    <Col
                        md={ displayAbout ? 8 : 9 }
                        xs={ displayAbout ? 6 : 8 }
                        { ...reviewBlock }
                    >
                        <Link to={ `/reviews/${review.slug}` }>
                            <h4 { ...gameTitle } { ...rules.title }>
                                {review.game.name}
                            </h4>
                        </Link>
                        {this.renderSummary()}
                    </Col>

                    {displayAbout ?
                        <Col md={ 2 } xs={ 2 } { ...divider }>
                              <div { ...reviewData }>
                                  <h4 { ...reviewDataTitle }>
                                      {'About the review'}
                                  </h4>

                                  <div { ...data }>

                                      <div { ...author }>
                                          <Contributor
                                              smallAvatar
                                              user={ review.author }
                                              title="Author"
                                              displayDetails
                                          />
                                      </div>
                                      <div { ...authorInline }>
                                          <Contributor
                                              noAvatar
                                              user={ review.author }
                                              title="Author"
                                              displayDetails
                                              inline
                                          />
                                      </div>

                                      {review.version ?
                                          <div { ...reviewDetail }>
                                                <span { ...flagIcon }>
                                                    <Flag
                                                        country={ review.version }
                                                    />
                                                </span>
                                                <span { ...versionRule }>
                                                    {`${versions[review.version]} version`}
                                                </span>
                                            </div> :
                                          null}

                                      {review.publishedAt ?
                                          <div
                                              { ...reviewDetail }
                                              data-tip
                                              data-for={ `time-tooltip${review.id}` }
                                          >
                                                <Tooltip
                                                    id={ `time-tooltip${review.id}` }
                                                    place="right"
                                                >
                                                    <span>
                                                        {moment(
                                                            new Date(
                                                                review.publishedAt
                                                            )
                                                        ).format('DD MMM YYYY')}
                                                    </span>
                                                </Tooltip>
                                                <span { ...dataIcon }>
                                                    <FontAwesome
                                                        name="clock-o"
                                                        { ...icon }
                                                    />
                                                </span>
                                                <span { ...dateRule }>
                                                    <span { ...publishedRule }>
                                                        {'Published '}
                                                    </span>
                                                    {moment(
                                                        new Date(
                                                            review.publishedAt
                                                        )
                                                    ).fromNow()}
                                                </span>
                                            </div> :
                                          null}
                                  </div>
                              </div>
                          </Col> :
                        null}
                </Row>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/content/review/Review.js